/**
 * Everything the guide reads (issue #9): the Character with its progression Choices opened, the
 * Entries it names, the catalogue, the sheet derived with every prompt's candidates, the class
 * tables and the attribute track. Built once per change; the guide's other modules are pure
 * functions over it.
 */
import type { Entry } from "../../shared/snapshot-format";
import type { Character, FoundationKind, LevelNumber } from "../character/model";
import { foundation } from "../character/model";
import type { Catalogue } from "../engine/catalogue";
import { deriveWithState, type DerivedSheet } from "../engine/engine";
import { familiarSlotTrack } from "../engine/familiar";
import { apparitionSlotTrack } from "../engine/spellcasting";
import type { EngineState } from "../engine/state";
import { attributeTrack, type AttributeTrack } from "./attributes";
import { prerequisiteContext, type PrerequisiteContext } from "./prerequisites";
import { openProgression, progressionTables, type ProgressionTables } from "./slots";

export interface GuideContext {
  /** The Character with every expected progression Choice present (save it when `changed`). */
  character: Character;
  entries: ReadonlyMap<string, Entry>;
  catalogue: Catalogue;
  /** Derived at the Character's Level with `prompts: "all"`, so answered ChoiceSets carry candidates too. */
  sheet: DerivedSheet;
  state: EngineState;
  tables: ProgressionTables;
  track: AttributeTrack;
  prerequisites: PrerequisiteContext;
  /** Whether `openProgression` changed the Character. */
  changed: boolean;
  /** Filled progression Choices the tables do not open, by key. */
  extra: Set<string>;
  /** The ancestry's slug and the slugs a versatile heritage adds: the traits an ancestry feat must carry. */
  ancestryTraits: string[];
  classSlug: string | null;
  /** The Dual Class second class's slug (#12), while the rule is on and the class chosen. */
  dualClassSlug: string | null;
}

/** Build the context. The Character is expected to have its grants reconciled already (`materialiseGrants`). */
export function buildGuideContext(input: Character, entries: ReadonlyMap<string, Entry>, catalogue: Catalogue): GuideContext {
  const first = deriveWithState({ character: input, entries, catalogue, prompts: "all" });
  const classEntry = entryOf(input, entries, "class");
  const dualClassEntry = input.variantRules.dualClass ? entryOf(input, entries, "dual-class") : undefined;
  const tables = progressionTables(classEntry, dualClassEntry);
  // The Familiar's ability slots (#13) come from the master's Rule Elements at every Level, so the guide opens them where they appear.
  tables.familiarSlots = familiarSlotTrack(first.state).opened;
  // The animist's apparition Choices (#21) come from the held class features the same way.
  tables.apparitionSlots = apparitionSlotTrack(first.state).opened;
  const track = attributeTrack(input, first.state.actor.system.build.attributes);
  const opened = openProgression(input, tables, track);
  const character = opened.character;
  // Opening empty Choices changes no derived value; the sheet stands.
  const { sheet, state } = opened.changed ? deriveWithState({ character, entries, catalogue, prompts: "all" }) : first;
  const ancestry = entryOf(character, entries, "ancestry");
  const heritage = entryOf(character, entries, "heritage");
  const ancestryTraits = ancestry ? [ancestry.slug] : [];
  if (heritage && heritage.system.ancestry === null) ancestryTraits.push(heritage.slug);
  return {
    character,
    entries,
    catalogue,
    sheet,
    state,
    tables,
    track: attributeTrack(character, state.actor.system.build.attributes),
    prerequisites: prerequisiteContext(sheet, catalogue, heritage?.name ?? null),
    changed: opened.changed,
    extra: new Set(opened.extra.map((e) => e.key)),
    ancestryTraits,
    classSlug: classEntry?.slug ?? null,
    dualClassSlug: dualClassEntry?.slug ?? null,
  };
}

export function entryOf(character: Character, entries: ReadonlyMap<string, Entry>, kind: FoundationKind): Entry | undefined {
  const uuid = foundation(character, kind)?.uuid;
  return uuid ? entries.get(uuid) : undefined;
}

/** The Level a Choice key names. */
export function levelOf(key: string): LevelNumber {
  return Number(key.split(":")[0]) as LevelNumber;
}
